import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { TbEdit } from "react-icons/tb";
import { Modal } from "antd";

import editIcon from "../../assets/icons/edit.svg";
import deleteIcon from "../../assets/icons/delete.svg";

import { useThunk } from "../../hooks/useThunk";
import { useInput } from "../../hooks/use-input";
import { useFileInput } from "../../hooks/use-file-input";
import {
  createArtist,
  getAllArtists,
  updateArtist,
  deleteArtist,
  fetchArtistSpotifyDetails,
  batchFetchSpotifyArtists,
} from "../../services/thunks/artistThunks";
import {
  uploadToCloudflare,
  deleteFromCloudflare,
} from "../../services/thunks/cloudFlareThunks";
import showToast from "../../utils/toastNotifications";
import { capitalizeEachWord } from "../../utils/formatting";
import { validateArtistName } from "../../utils/validationUtils";
import { processImageFile } from "../../utils/processImageFile";
import { roles } from "../../constants/commonConstants";
import { fetchExternalImageAsFile } from "../../utils/fetchExternalImageAsFile";

import Input from "../../components/Form/Input";
import CustomButton from "../../components/Buttons/CustomButton";
import Table from "../../components/Table/Table";
import ConfirmDeleteModal from "../../components/Modals/ConfirmDeleteModal";
import FileUpload from "../../components/Form/FileUpload";
import SearchBar from "../../components/common/SearchBar";
import CloudflareImage from "../../components/CloudflareImage";
import FilterDropdown from "../../components/FilterDropdown";

const ManageArtists = () => {
  const auth = useSelector((state) => state.auth);
  const { role } = auth;
  const isRemixer = role === roles.REMIXER;

  const [doCreateArtist, isCreatingArtist] = useThunk(createArtist);
  const [doUpdateArtist, isUpdatingArtist] = useThunk(updateArtist);
  const [doDeleteArtist, isDeletingArtist] = useThunk(deleteArtist);
  const [doUploadToCloudflare, isUploadingToCloudflare] =
    useThunk(uploadToCloudflare);
  const [doDeleteFromCloudflare, isDeletingFromCloudflare] =
    useThunk(deleteFromCloudflare);
  const [doFetchArtistSpotifyDetails, isFetchingArtistSpotifyDetails] =
    useThunk(fetchArtistSpotifyDetails);
  const [doBatchFetchSpotifyArtists, isBatchFetchingSpotifyArtists] = useThunk(
    batchFetchSpotifyArtists,
  );

  const {
    value: artistName,
    handleInputChange: handleArtistNameChange,
    handleInputBlur: handleArtistNameBlur,
    hasError: artistNameHasError,
    errorMessage: artistNameErrorMessage,
    setErrorMessage,
    reset: resetArtistName,
  } = useInput("", (value) => validateArtistName(value.trim()));

  const {
    file: artistProfilePicture,
    handleFileDrop: handleArtistImageDrop,
    error: artistImageError,
    reset: resetArtistImage,
  } = useFileInput();

  const [editingId] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [rowStates, setRowStates] = useState({});
  const [filterOption, setFilterOption] = useState("all");
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const [artistImageErrorMessage, setArtistImageErrorMessage] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  const tableRef = useRef(null);
  const pageSetterRef = useRef(null);
  const tableUpdateRowRef = useRef(null);
  const tableRemoveRowRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tableRef.current && !tableRef.current.contains(event.target)) {
        setRowStates({});
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSubmit = async () => {
    if (!isValidForm) {
      return;
    }

    if (isRemixer && !artistProfilePicture) {
      setArtistImageErrorMessage("Artist Image is Required");
      return;
    }

    const formattedName = capitalizeEachWord(artistName.trim());

    try {
      let profilePictureUrl = artistProfilePicture;

      if (
        profilePictureUrl &&
        typeof profilePictureUrl === "object" &&
        profilePictureUrl.url &&
        !profilePictureUrl.key
      ) {
        // External Spotify URL - download and upload to Cloudflare
        try {
          const imageFile = await fetchExternalImageAsFile(
            profilePictureUrl.url,
            formattedName,
          );
          const uploadResult = await doUploadToCloudflare({
            file: imageFile,
            type: "images",
          });
          const url =
            uploadResult?.response?.url ||
            uploadResult?.data?.url ||
            uploadResult?.payload?.url ||
            uploadResult?.url;
          if (!uploadResult.success || !url) {
            throw new Error(
              uploadResult.error || "Failed to upload fetched image",
            );
          }
          profilePictureUrl = url;
        } catch (err) {
          console.error("Failed to process Spotify image:", err);
          showToast(
            "warning",
            "Failed to process Spotify image. Using fallback.",
          );
          profilePictureUrl = profilePictureUrl.url;
        }
      } else if (artistProfilePicture instanceof File) {
        const uploadResult = await doUploadToCloudflare({
          file: profilePictureUrl,
          type: "images",
        });

        const url =
          uploadResult?.response?.url ||
          uploadResult?.data?.url ||
          uploadResult?.payload?.url ||
          uploadResult?.url;

        if (!uploadResult.success || !url) {
          throw new Error(uploadResult.error || "Failed to upload image");
        }

        profilePictureUrl = url;
      }

      const result = await doCreateArtist({
        token: auth.token,
        payload: {
          artistName: formattedName,
          artistProfilePicture: profilePictureUrl,
        },
      });
      if (result.success) {
        const newId = result.response?.data?.artist?._id || result.id;
        const newIndex = result.response?.data?.index;
        showToast("success", `Artist ‘${formattedName}’ added successfully.`);
        resetArtistName();
        resetArtistImage();
        setFileUploadKey((prev) => prev + 1);

        const pageSize = 10;
        const pageNumber = Math.floor(newIndex / pageSize) + 1;

        if (pageSetterRef.current) {
          pageSetterRef.current(pageNumber);
        }

        setRefreshTrigger((prev) => prev + 1);

        setHighlightedRowId(newId);

        setTimeout(() => {
          setHighlightedRowId(null);
        }, 5000);
      } else {
        setErrorMessage(result.error.message);
      }
    } catch (error) {
      showToast("error", error.message || "An error occurred");
    }
  };

  const handleDelete = async (record) => {
    const result = await doDeleteArtist({ token: auth.token, id: record._id });
    if (result.success) {
      showToast("success", "Artist deleted successfully.");

      if (tableRemoveRowRef.current) {
        tableRemoveRowRef.current(record._id);
      }
      const updatedNA = result.response?.data?.naArtist;
      if (updatedNA && tableUpdateRowRef.current) {
        tableUpdateRowRef.current(updatedNA._id, updatedNA);
      }

      if (record.artistProfilePicture) {
        const imageUrl = record.artistProfilePicture;
        const imageKey = imageUrl ? imageUrl.split("/").pop() : null;
        const deleteResult = await doDeleteFromCloudflare({ key: imageKey });
        if (!deleteResult.success) {
          showToast(
            "error",
            deleteResult.error.message ||
              "Failed to delete image from Cloudflare",
          );
        }
      }
    } else {
      showToast("error", result.error.message);
    }
  };

  const handleBatchFetchSpotify = async () => {
    // Prevent users from accidentally navigating away or refreshing
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    try {
      setIsPolling(true);
      // Step 1: Start the batch job — backend returns 202 immediately with jobId
      const result = await doBatchFetchSpotifyArtists({ token: auth.token });

      if (!result.success) {
        setIsPolling(false);
        showToast("error", result.error?.message || "Batch fetch failed. Retry.");
        return;
      }

      const responseData = result.response?.data || result.data || result;
      const { jobId, totalArtists } = responseData;

      // If no artists need updating, the server can still return 200 with no jobId
      if (!jobId) {
        showToast("info", "No artists found with missing images.");
        return;
      }

      showToast(
        "info",
        `Batch job started for ${totalArtists} artist${totalArtists !== 1 ? "s" : ""}. Processing in background…`,
      );

      // Step 2: Poll status with exponential backoff (e.g., 3s -> 4s -> 6s ... up to 15s)
      const INITIAL_POLL_DELAY = 3000;
      const MAX_POLL_DELAY = 15000;
      const BACKOFF_FACTOR = 1.4;
      const MAX_WAIT_MS = 20 * 60 * 1000; // 20 minutes hard cap
      const startedAt = Date.now();

      await new Promise((resolve) => {
        let currentDelay = INITIAL_POLL_DELAY;

        const poll = async () => {
          if (Date.now() - startedAt > MAX_WAIT_MS) {
            showToast(
              "warning",
              "Batch job is taking longer than expected. Check the artists list later.",
            );
            resolve();
            return;
          }

          try {
            const statusResult = await doBatchFetchSpotifyArtists({
              token: auth.token,
              pollJobId: jobId,
            });

            const statusData =
              statusResult.response?.data || statusResult.data || statusResult;


            if (
              statusData.status === "completed" ||
              statusData.status === "failed"
            ) {
              // Final check with logs
              const finalStatusResult = await doBatchFetchSpotifyArtists({
                token: auth.token,
                pollJobId: jobId,
                includeLogs: true,
              });
              const finalData =
                finalStatusResult.response?.data ||
                finalStatusResult.data ||
                finalStatusResult;


              const { summary } = finalData;
              const { totalProcessed, successfulUpdates, failedUpdates } =
                summary || {};

              if (totalProcessed === 0) {
                showToast("info", "No artists found with missing images.");
              } else if (
                successfulUpdates === totalProcessed &&
                totalProcessed > 0
              ) {
                showToast(
                  "success",
                  `Batch fetch complete: All ${successfulUpdates} artist${successfulUpdates !== 1 ? "s" : ""} updated successfully.`,
                );
              } else if (successfulUpdates === 0 && failedUpdates > 0) {
                const errorDetail = statusData.lastError
                  ? ` (${statusData.lastError})`
                  : "";
                showToast(
                  "error",
                  `Batch fetch failed. ${failedUpdates} artists could not be updated${errorDetail}. Retry.`,
                );
              } else {
                showToast(
                  "warning",
                  `Batch fetch done: ${successfulUpdates} updated, ${failedUpdates} failed out of ${totalProcessed}.`,
                );
              }

              setRefreshTrigger((prev) => prev + 1);
              resolve();
            } else {
              // Still running - increase delay and poll again
              currentDelay = Math.min(
                currentDelay * BACKOFF_FACTOR,
                MAX_POLL_DELAY,
              );
              setTimeout(poll, currentDelay);
            }
          } catch (err) {
            console.error("Polling error:", err);
            // On error, wait and try again with same delay
            setTimeout(poll, currentDelay);
          }
        };

        // Start first poll after initial delay
        setTimeout(poll, INITIAL_POLL_DELAY);
      });
    } finally {
      setIsPolling(false);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  };

  const [searchQuery, setSearchQuery] = useState("");

  const handleFilterChange = (value) => {
    setFilterOption(value);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleRowSave = React.useCallback(
    async (id, value, image) => {
      setEditMode(true);

      const artistName = capitalizeEachWord(value.trim());
      let profilePictureUrl = image;

      if (image instanceof File) {
        const uploadResult = await doUploadToCloudflare({
          file: image,
          type: "images",
        });

        const url =
          uploadResult?.response?.url ||
          uploadResult?.data?.url ||
          uploadResult?.payload?.url ||
          uploadResult?.url;

        if (!uploadResult.success || !url) {
          showToast("error", uploadResult.error || "Failed to upload image");
          return;
        }
        profilePictureUrl = url;
      }

      const result = await doUpdateArtist({
        token: auth.token,
        id,
        payload: {
          artistName: artistName,
          artistProfilePicture: profilePictureUrl,
        },
      });

      if (result.success) {
        showToast("success", "Artist updated successfully.");
        setRowStates((prev) => ({
          ...prev,
          [id]: { editing: false, value: undefined, image: undefined },
        }));

        if (tableUpdateRowRef.current) {
          tableUpdateRowRef.current(id, {
            artistName,
            artistProfilePicture: profilePictureUrl,
          });
        }

        setHighlightedRowId(id);
        setTimeout(() => {
          setHighlightedRowId(null);
        }, 5000);
        setEditMode(false);
      } else {
        showToast("error", result.error.message);
        setEditMode(false);
      }
    },
    [
      doUploadToCloudflare,
      doUpdateArtist,
      auth.token,
      setRowStates,
      setHighlightedRowId,
    ],
  );

  const handleSearch = () => {
    if (pageSetterRef.current) {
      pageSetterRef.current(1);
    }
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (pageSetterRef.current) {
        pageSetterRef.current(1);
      }
      setRefreshTrigger((prev) => prev + 1);
    }
  };

  const handleFetchSpotifyDetails = async () => {
    if (!artistName.trim()) {
      showToast("error", "Please enter an artist name first.");
      return;
    }

    const res = await doFetchArtistSpotifyDetails({
      token: auth.token,
      artistName: artistName.trim(),
    });

    const artistData = res.response?.data?.data || res.response?.data;

    if (res.success && artistData?.artistProfilePicture) {
      const spotifyImageUrl = artistData.artistProfilePicture;
      handleArtistImageDrop({ url: spotifyImageUrl });
      showToast("success", "Artist profile picture fetched from Spotify.");
    } else {
      showToast(
        "error",
        res.error?.message || "Failed to fetch details from Spotify.",
      );
    }
  };

  const isValidForm = artistName && !artistNameHasError;

  const tableColumns = useMemo(() => {
    const columns = [
      {
        title: "Profile",
        dataIndex: "artistProfilePicture",
        key: "artistProfilePicture",
        align: "center",
        minWidth: "350px",
        render: (artistProfilePicture, record) => {
          const isEditing = rowStates[record._id]?.editing;
          const image = rowStates[record._id]?.image ?? artistProfilePicture;
          const error = rowStates[record._id]?.error;

          if (isEditing) {
            return (
              <div className="flex flex-row items-start ml-10">
                <div className="relative group w-[40px] h-[40px]">
                  <CloudflareImage imageUrl={image} alt="Profile" />
                  <button
                    onClick={() =>
                      document.getElementById(`fileInput-${record._id}`).click()
                    }
                    className="absolute bottom-0 right-0 p-1 bg-slate-700 hover:bg-black text-white rounded-full transition"
                  >
                    <TbEdit className="text-white text-sm" />
                  </button>
                  <input
                    id={`fileInput-${record._id}`}
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => {
                      const selectedFile = e.target.files[0];
                      if (!selectedFile) return;

                      if (selectedFile.size > 3 * 1024 * 1024) {
                        showToast(
                          "error",
                          "Image exceeds maximum allowed size of 3MB.",
                        );
                        return;
                      }

                      setRowStates((prev) => ({
                        ...prev,
                        [record._id]: {
                          ...prev[record._id],
                          error: null,
                        },
                      }));

                      processImageFile(selectedFile, {
                        targetWidth: 500,
                        targetHeight: 500,
                        aspectRatio: 1,
                        quality: 0.75,
                      })
                        .then((formattedFile) => {
                          if (formattedFile.size > 150 * 1024) {
                            showToast(
                              "error",
                              "Compressed image exceeds 150KB.",
                            );
                            return;
                          }

                          setRowStates((prev) => ({
                            ...prev,
                            [record._id]: {
                              ...prev[record._id],
                              image: formattedFile,
                              error: null,
                            },
                          }));
                        })
                        .catch((err) => {
                          showToast(
                            "error",
                            err.message || "Image processing failed",
                          );
                        });
                    }}
                  />
                </div>
                <div className="ml-4 text-red-500 text-xs mt-2 max-w-[200px] break-words">
                  {error}
                </div>
              </div>
            );
          }

          return artistProfilePicture ? (
            <div className="flex justify-start items-center ml-10">
              <CloudflareImage imageUrl={artistProfilePicture} alt="Profile" />
            </div>
          ) : (
            <div className="ml-8 text-[#888] h-[40px] flex items-center text-sm italic">
              No Image
            </div>
          );
        },
      },
      {
        title: "Artist Name",
        dataIndex: "artistName",
        key: "artistName",
        minWidth: "300px",
        render: (text, record) => {
          const isEditing = rowStates[record._id]?.editing;
          const value = rowStates[record._id]?.value ?? text;

          return isEditing ? (
            <Input
              autoFocus
              type="text"
              value={value}
              onChange={(e) => {
                const newValue = e.target.value;
                setRowStates((prev) => ({
                  ...prev,
                  [record._id]: {
                    ...prev[record._id],
                    value: newValue,
                  },
                }));
              }}
              inputStyle="bg-transparent border-0 border-b border-[#ccc] rounded-none shadow-none outline-none px-0 pb-0 pt-[1px] h-[28px] text-sm [400] leading-[18px] text-[#E2E2E2]font-inter"
            />
          ) : (
            <div className="min-h-[32px] flex items-center text-sm font-[400] font-inter text-[#E2E2E2]">
              {text}
            </div>
          );
        },
      },
      {
        title: "Used In",
        dataIndex: "usedIn",
        key: "usedIn",
        align: "center",
        minWidth: "200px",
        render: (count) => (
          <div className="flex justify-center items-center bg-[#212121] rounded-[10px] w-[50px] h-[25px] leading-[24px] text-[#E2E2E2] text-sm text-center font-[400] font-inter">
            {count || 0}
          </div>
        ),
      },
    ];
    if (!isRemixer) {
      columns.push({
        title: "Action",
        key: "action",
        minWidth: "160px",
        render: (_, record) => {
          const isEditing = rowStates[record._id]?.editing;
          const value = rowStates[record._id]?.value ?? record.artistName;
          const image =
            rowStates[record._id]?.image ?? record.artistProfilePicture;

          return (
            <div className="flex gap-3 justify-start items-center">
              {isEditing ? (
                <>
                  <CustomButton
                    onClick={() =>
                      setRowStates((prev) => ({
                        ...prev,
                        [record._id]: {
                          editing: false,
                          value: undefined,
                          image: undefined,
                        },
                      }))
                    }
                    className="flex items-center justify-center gap-2 px-4 py-1 border border-[#CCCCCC] rounded-[15px] text-xs font-normal text-[#CCCCCC] w-[90px] hover:bg-[#333] transition font-inter"
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    onClick={() => handleRowSave(record._id, value, image)}
                    className="flex items-center justify-center gap-2 px-4 py-1 border border-[#00ffd5] rounded-[15px] text-xs font-normal text-[#00ffd5] w-[90px] hover:bg-[#00ffd53b] hover:text-white transition font-inter"
                    disabled={
                      !!rowStates[record._id]?.error ||
                      !value ||
                      isUpdatingArtist ||
                      isUploadingToCloudflare
                    }
                    loading={isUpdatingArtist || isUploadingToCloudflare}
                  >
                    Save
                  </CustomButton>
                </>
              ) : record.artistName.toLowerCase() === "n/a" ? (
                <div className="text-xs text-[#888] italic">Locked</div>
              ) : (
                <>
                  <CustomButton
                    onClick={() => {
                      setRowStates({
                        [record._id]: {
                          editing: true,
                          value: record.artistName,
                          image: record.artistProfilePicture,
                        },
                      });
                    }}
                    className="flex items-center justify-center gap-2 px-4 py-1 border border-[#B4C007] text-[#B4C007] rounded-[15px] text-xs font-normal w-[90px] hover:bg-[#333300] transition font-inter"
                  >
                    <img src={editIcon} alt="edit" className="w-3 h-3" />
                    Edit
                  </CustomButton>
                  <CustomButton
                    onClick={() => {
                      setSelectedArtist(record);
                      setShowConfirm(true);
                    }}
                    className="flex items-center justify-center gap-2 px-4 py-1 border border-[#C00707] text-[#C00707] rounded-[15px] text-xs font-normal w-[90px] hover:bg-[#330000] transition font-inter"
                  >
                    <img src={deleteIcon} alt="delete" className="w-3 h-3" />
                    Delete
                  </CustomButton>
                </>
              )}
            </div>
          );
        },
      });
    }

    return columns;
  }, [
    rowStates,
    isRemixer,
    isUpdatingArtist,
    isUploadingToCloudflare,
    handleRowSave,
  ]);

  return (
    <div className="px-6 py-10 text-white min-h-screen">
      {/* Form Section */}
      <div className="bg-[#111111D6] border border-[#505050] p-6 rounded-lg mb-8">
        <h2 className="text-xl font-bold font-['Public_Sans'] mb-4 text-[#E3E3E3]">
          {editingId ? "Edit Artist" : "Add New Artists"}
        </h2>

        <div className="flex flex-col gap-4 xl:flex-row xl:gap-5 xl:h-[70px] items-start xl:items-end">
          <div className="flex-1 w-full relative">
            <Input
              label="Artist Name"
              isRequired
              placeholder="Enter Artist Name"
              value={artistName}
              onChange={handleArtistNameChange}
              onBlur={handleArtistNameBlur}
              errorMessage={artistNameErrorMessage}
              inputStyle="bg-[#45444433] border-[1px] border-[#808080] h-[45px] pr-[110px]"
              outerContainerStyle="mt-[-1px]"
              labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
              placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
              disabled={
                isBatchFetchingSpotifyArtists ||
                isCreatingArtist ||
                isUpdatingArtist
              }
            />
            <div className="absolute right-0 bottom-0.5 mb-[1px] mr-[1px]">
              <CustomButton
                onClick={handleFetchSpotifyDetails}
                loading={isFetchingArtistSpotifyDetails}
                textSize="text-[14px]"
                padding="py-2.5 px-4"
                width="w-auto"
                height="h-full"
                borderColor="border-none"
                rounded="rounded-r-md rounded-l-none"
                disabled={
                  !artistName.trim() ||
                  isFetchingArtistSpotifyDetails ||
                  isBatchFetchingSpotifyArtists
                }
                className="bg-[#262626] text-[#878787] font-bold border-l border-[#808080]"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14 16H19V21M10 8H5.00003V3M19.418 9.003C18.8574 7.61519 17.9186 6.41251 16.7084 5.53166C15.4983 4.65082 14.0653 4.12714 12.5723 4.02016C11.0793 3.91318 9.5863 4.22718 8.26291 4.92648C6.93952 5.62577 5.83886 6.68231 5.08603 7.976M4.58203 14.997C5.14283 16.3846 6.0816 17.587 7.29166 18.4676C8.50172 19.3483 9.93455 19.8718 11.4273 19.9788C12.9201 20.0858 14.4129 19.7719 15.7362 19.0728C17.0595 18.3737 18.1601 17.3174 18.913 16.024"
                    stroke="#878787"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Fetch
              </CustomButton>
            </div>
          </div>

          <div className="flex-1 w-full min-w-[409px]">
            <FileUpload
              label="Profile Image"
              key={fileUploadKey}
              type="image"
              maxSize={3}
              isRequired={isRemixer}
              uploadText="Select your file or drag and drop here"
              onFileUpload={(file) => {
                handleArtistImageDrop(file);
                setArtistImageErrorMessage("");
              }}
              allowRemove={true}
              defaultValue={artistProfilePicture}
              showPreview={true}
              error={artistImageErrorMessage || artistImageError}
              showErrorBelow={false}
              containerStyle="min-w-[409px] w-full h-full "
              labelStyle="text-sm mb-[8px] font-medium font-['Public_Sans'] text-[#E3E3E3] whitespace-nowrap"
              disabled={
                isBatchFetchingSpotifyArtists ||
                isCreatingArtist ||
                isUpdatingArtist
              }
            />
          </div>

          {/* Submit Button */}
          <CustomButton
            useGradientBorder
            rounded="rounded-lg"
            textSize="text-[18px] leading-[24px]"
            padding="py-[10.5px] px-[13.5px]"
            font="font-public-sans"
            border="border-lg"
            type="button"
            width="w-[131px] h-[42px]"
            onClick={handleSubmit}
            disabled={
              !isValidForm ||
              isCreatingArtist ||
              isUploadingToCloudflare ||
              isBatchFetchingSpotifyArtists
            }
            loading={isCreatingArtist || (!editMode && isUploadingToCloudflare)}
          >
            {editingId ? "Update Artist" : "Add Artist"}
          </CustomButton>
        </div>
      </div>

      {/* Search Section */}
      <div className="flex items-center gap-4 mb-6">
        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setRefreshTrigger={setRefreshTrigger}
          placeholder="Search Artists..."
          containerClass="bg-[#45444433] border border-[#808080] rounded-[5px] px-3 w-full h-[45px]"
          inputClass="bg-transparent outline-none text-sm ml-3 w-full text-[#E2E2E2] font-[400] font-['Public Sans'] pr-6"
          buttonClass="absolute right-3 text-[#A0A0A0] hover:text-white text-lg font-bold"
          onKeyPress={handleKeyPress}
        />

        {/* Filter Dropdown */}
        <FilterDropdown
          filterValue={filterOption}
          onSelect={(option) => handleFilterChange(option._id)}
          isSearchable={false}
        />

        <CustomButton
          className="rounded-[3.77px] h-[45px] w-[91px] min-w-[91px] text-[11px] uppercase font-['Public_Sans'] font-normal"
          textColor="text-[#51F9EF]"
          borderColor="border-[#51F9EF]"
          onClick={handleSearch}
        >
          Search
        </CustomButton>

        <CustomButton
          className="rounded-[3.77px] h-[45px] px-4 min-w-fit text-[11px] uppercase font-['Public_Sans'] font-normal"
          textColor="text-[#51F9EF]"
          borderColor="border-[#51F9EF]"
          onClick={handleBatchFetchSpotify}
          loading={isBatchFetchingSpotifyArtists}
          disabled={isBatchFetchingSpotifyArtists}
        >
          Batch Fetch Missing Images
        </CustomButton>
      </div>

      {/* Table Section */}
      <div ref={tableRef}>
        <Table
          columns={tableColumns}
          fetchData={getAllArtists}
          refreshTrigger={refreshTrigger}
          rowKey="_id"
          dataKey="items"
          headingBgColor="#0D141B"
          headingTextColor="#FFFFFF"
          customQueryParams={(params) => ({
            ...params,
            token: auth.token,
            artistName: searchQuery.trim() || undefined,
            hasProfilePicture:
              filterOption === "withProfilePic"
                ? true
                : filterOption === "withoutProfilePic"
                  ? false
                  : undefined,
          })}
          highlightedRowId={highlightedRowId}
          setPageCallback={(fn) => (pageSetterRef.current = fn)}
          searchQuery={searchQuery}
          updateRowRef={tableUpdateRowRef}
          removeRowRef={tableRemoveRowRef}
        />
      </div>

      <ConfirmDeleteModal
        visible={showConfirm}
        onConfirm={async () => {
          await handleDelete(selectedArtist);
          setShowConfirm(false);
          setSelectedArtist(null);
        }}
        onCancel={() => {
          setShowConfirm(false);
          setSelectedArtist(null);
        }}
        title="Confirm Deletion"
        content={`Are you sure you want to delete the artist "${selectedArtist?.artistName}"?`}
        disabled={isDeletingArtist || isDeletingFromCloudflare}
        loading={isDeletingArtist || isDeletingFromCloudflare}
      />

      <Modal
        visible={isPolling}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        modalRender={() => (
          <div className="bg-[#121B24] border border-[#2A3543] rounded-lg p-8 text-center shadow-2xl min-w-[400px]">
            <div className="flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#51F9EF] mb-4"></div>
              <h3 className="text-lg font-bold text-white mb-2">
                Processing Batch Update
              </h3>
              <p className="text-[#878787]">
                Searching Spotify and uploading profile images to Cloudflare.
                This may take a moment...
              </p>
              <div className="mt-6 px-4 py-2 bg-[#1B252F] rounded text-[#FFD700] text-xs font-medium border border-[#FFD70033]">
                ⚠️ Please do not refresh or close this tab
              </div>
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default ManageArtists;
